import React from 'react'

import { Row as GridRow } from 'react-table'
import styled, { css } from 'styled-components'

import { palette, transitions } from '_styles/variables'

import { parseData } from '../helpers'
import { Cell } from './Cell'

interface RowProps extends React.ComponentPropsWithoutRef<'tr'> {
  expandedRowRenderer?(): JSX.Element | null
  isExpanded?: boolean
}

interface RowMapperProps<D extends object> {
  page: GridRow<D>[]
  defaultMaxLines?: number
  prepareRow(row: GridRow<D>): void
  toggleAllRowsExpanded(value?: boolean): void
  expandedRowRenderer?(data?: D, isExpanded?: boolean): JSX.Element | null
  showColumnDividers?: boolean
}

export const HeaderRow = styled.tr`
  height: 2rem;
  border: 1px solid ${palette.neutral['grey-ebebeb']};
  white-space: nowrap;
`

const ExpandedWrapper = styled.tr<{ isExpanded?: boolean }>`
  display: block;
  max-height: ${({ isExpanded }) => (isExpanded ? '30rem' : 0)};
  overflow: hidden;
  /* transition: ${transitions.quick(['max-height'])}; */

  & td {
    display: block;
  }
`

const StyledRow = styled.tr<{ isExpanded?: boolean; canExpand?: boolean }>`
  height: 3.75rem;
  border: 1px solid ${palette.neutral['grey-ebebeb']};
  border-top: none;

  &:nth-child(${({ canExpand }) => (canExpand ? '4n - 1' : '2n')}) {
    background-color: ${palette.neutral['grey-f6f7fa']};
  }

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      border-left: 2px solid black;
      border-bottom: none;
    `}
`

export const Row: React.FC<RowProps> = ({
  isExpanded,
  children,
  expandedRowRenderer,
  ...props
}) => {
  const canExpand = Boolean(expandedRowRenderer)

  return (
    <>
      <StyledRow
        {...props}
        aria-expanded={isExpanded}
        canExpand={canExpand}
        isExpanded={isExpanded}
      >
        {children}
      </StyledRow>
      {expandedRowRenderer && (
        <ExpandedWrapper aria-hidden={!isExpanded} isExpanded={isExpanded}>
          <td>{expandedRowRenderer()}</td>
        </ExpandedWrapper>
      )}
    </>
  )
}

export const RowMapper = <D extends object>({
  defaultMaxLines,
  showColumnDividers,
  page,
  prepareRow,
  expandedRowRenderer,
  toggleAllRowsExpanded,
}: RowMapperProps<D>) => (
  <>
    {page.map(row => {
      prepareRow(row)

      const getter = expandedRowRenderer && row.getToggleRowExpandedProps()
      const { key: rowKey, ...rowProps } = row.getRowProps(getter)

      const onClick =() => {
        if (expandedRowRenderer) {
          toggleAllRowsExpanded(false)
          row.toggleRowExpanded(!row.isExpanded)
        }
      }

      const rowRenderer =
        () =>
          expandedRowRenderer
            ? expandedRowRenderer(row.original, row.isExpanded)
            : null

      return (
        <Row
          {...rowProps}
          key={rowKey}
          isExpanded={row.isExpanded}
          expandedRowRenderer={rowRenderer}
          onClick={onClick}
        >
          {row.cells.map(cell => {
            const cellProps = {
                ...cell.getCellProps(),
                title: parseData(cell.value),
              }
            const { key } = cellProps

            return (
              <Cell
                key={key}
                cell={cell}
                cellProps={cellProps}
                defaultMaxLines={defaultMaxLines}
                showColumnDividers={showColumnDividers}
              />
            )
          })}
        </Row>
      )
    })}
  </>
)
